/**
 * blueprint-templates.ts — Catalogue complet des templates Blueprint
 * 141 templates existants + 68 nouveaux identifies par DeepSearch
 * Total: 209 templates couvrant 12 departements
 *
 * Source existants: data/templates-documentaires.json (141)
 * Source nouveaux: docs/DEEPSEARCH-GAPS-TEMPLATES-RESULTS.md (68)
 */

// ── Types ──

export type TemplateCategory = "strategique" | "operationnel" | "conformite" | "diagnostic";
export type TemplatePhase = "startup" | "scaleup" | "exitup";
export type TemplateSource = "existant" | "nouveau";

export interface BlueprintTemplate {
  id: string;
  name: string;
  description: string;
  botCode: string;
  blueprintSection: string;
  category: TemplateCategory;
  phases: TemplatePhase[];
  minTier: number;
  docForgeReady: boolean;
  source: TemplateSource;
}

// ── Mapping helpers (categorie brute -> TemplateCategory) ──

function mapCategory(raw: string): TemplateCategory {
  const map: Record<string, TemplateCategory> = {
    // strategique
    plan: "strategique",
    politique: "strategique",
    guide: "strategique",
    manuel: "strategique",
    reference: "strategique",
    matrice: "strategique",
    // operationnel
    rapport: "operationnel",
    analyse: "operationnel",
    tableau: "operationnel",
    dashboard: "operationnel",
    formulaire: "operationnel",
    grille: "operationnel",
    fiche: "operationnel",
    cahier: "operationnel",
    procedure: "operationnel",
    template: "operationnel",
    bilan: "operationnel",
    tableau_de_bord: "operationnel",
    // conformite
    registre: "conformite",
    contrat: "conformite",
    dossier: "conformite",
    // diagnostic
    audit: "diagnostic",
  };
  return map[raw] ?? "operationnel";
}

// ── Templates existants (141) ──

const EXISTING_TEMPLATES: BlueprintTemplate[] = [
  // ── CFOB — Finance (12) ──
  {
    id: "TPL-CFOB-001",
    name: "Analyse de couts de revient (Costing)",
    description: "Calcul precis du cout de fabrication par unite pour assurer une marge saine.",
    botCode: "CFOB",
    blueprintSection: "modele_revenus",
    category: "operationnel",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CFOB-002",
    name: "Analyse de rentabilite par ligne de produits",
    description: "Analyse des marges par produit pour decider ce qu'on pousse ou retire.",
    botCode: "CFOB",
    blueprintSection: "modele_revenus",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CFOB-003",
    name: "Analyse de scenarios financiers (Best/Base/Worst)",
    description: "Modelisation des impacts sur le BAIIA selon divers evenements de marche.",
    botCode: "CFOB",
    blueprintSection: "budget_previsions",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CFOB-004",
    name: "Dossier de demande de financement (Subvention/Pret)",
    description: "Document formate pour presenter un projet a Investissement Quebec ou la BDC.",
    botCode: "CFOB",
    blueprintSection: "fiscalite",
    category: "conformite",
    phases: ["startup", "scaleup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CFOB-005",
    name: "Business plan / Plan d'affaires",
    description: "Document complet decrivant le modele d'affaires, le marche et les previsions sur 3-5 ans.",
    botCode: "CFOB",
    blueprintSection: "budget_previsions",
    category: "strategique",
    phases: ["startup", "scaleup"],
    minTier: 1,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CFOB-006",
    name: "Plan d'investissement en capital (CAPEX)",
    description: "Planification des investissements en equipements et infrastructures.",
    botCode: "CFOB",
    blueprintSection: "budget_previsions",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CFOB-007",
    name: "Politique de gestion des comptes recevables",
    description: "Politique de credit et de recouvrement pour optimiser le fonds de roulement.",
    botCode: "CFOB",
    blueprintSection: "tresorerie",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CFOB-008",
    name: "Previsions de tresorerie (Cashflow)",
    description: "Projection du cashflow sur 13 semaines pour anticiper les besoins de liquidite.",
    botCode: "CFOB",
    blueprintSection: "tresorerie",
    category: "operationnel",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 1,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CFOB-009",
    name: "Rapport financier mensuel",
    description: "Rapport resumant les resultats financiers du mois avec analyse des ecarts.",
    botCode: "CFOB",
    blueprintSection: "etats_financiers",
    category: "operationnel",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CFOB-010",
    name: "Rapport de variance budget vs reel",
    description: "Analyse des ecarts entre le budget et les resultats reels par poste.",
    botCode: "CFOB",
    blueprintSection: "budget_previsions",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CFOB-011",
    name: "Tableau de bord financier (KPI)",
    description: "Vue d'ensemble des indicateurs financiers cles pour le dirigeant.",
    botCode: "CFOB",
    blueprintSection: "kpis_finance",
    category: "operationnel",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 1,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CFOB-012",
    name: "Dossier Justificatif CRIC (Recherche, Innovation et Commercialisation)",
    description: "Documentation technique pour les credits d'impot CRIC du Quebec.",
    botCode: "CFOB",
    blueprintSection: "fiscalite",
    category: "conformite",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: false,
    source: "existant",
  },

  // ── CMOB — Marketing (11) ──
  {
    id: "TPL-CMOB-001",
    name: "Analyse du parcours client (Customer Journey Map)",
    description: "Cartographie complete du parcours client, de la decouverte a la fidelisation.",
    botCode: "CMOB",
    blueprintSection: "personas_icp",
    category: "operationnel",
    phases: ["startup", "scaleup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CMOB-002",
    name: "Analyse de positionnement concurrentiel",
    description: "Evaluation du positionnement de la marque vs la concurrence.",
    botCode: "CMOB",
    blueprintSection: "positionnement",
    category: "operationnel",
    phases: ["startup", "scaleup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CMOB-003",
    name: "Audit de presence numerique",
    description: "Evaluation complete de la presence en ligne (site, reseaux sociaux, SEO).",
    botCode: "CMOB",
    blueprintSection: "canaux_budget",
    category: "diagnostic",
    phases: ["startup", "scaleup"],
    minTier: 1,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CMOB-004",
    name: "Brief creatif (Campagne/Projet)",
    description: "Brief standardise pour lancer un projet creatif ou une campagne marketing.",
    botCode: "CMOB",
    blueprintSection: "contenu_campagnes",
    category: "operationnel",
    phases: ["startup", "scaleup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CMOB-005",
    name: "Persona clients (Profils-types)",
    description: "Definition des profils clients ideaux avec besoins, motivations et freins.",
    botCode: "CMOB",
    blueprintSection: "personas_icp",
    category: "strategique",
    phases: ["startup", "scaleup"],
    minTier: 1,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CMOB-006",
    name: "Plan de communication de crise",
    description: "Protocole de communication en cas de crise mediatique ou organisationnelle.",
    botCode: "CMOB",
    blueprintSection: "reputation",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CMOB-007",
    name: "Plan de lancement de produit (Go-To-Market)",
    description: "Strategie complete de mise en marche d'un nouveau produit ou service.",
    botCode: "CMOB",
    blueprintSection: "contenu_campagnes",
    category: "strategique",
    phases: ["startup", "scaleup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CMOB-008",
    name: "Plan de marque employeur (Co-RH)",
    description: "Strategie pour attirer et retenir les talents via la marque employeur.",
    botCode: "CMOB",
    blueprintSection: "positionnement",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CMOB-009",
    name: "Rapport de performance des campagnes",
    description: "Suivi des KPI des campagnes marketing avec analyse du ROI par canal.",
    botCode: "CMOB",
    blueprintSection: "kpis_marketing",
    category: "operationnel",
    phases: ["startup", "scaleup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CMOB-010",
    name: "Strategie de contenu (Content Calendar)",
    description: "Planification editoriale trimestrielle avec calendrier de publication.",
    botCode: "CMOB",
    blueprintSection: "contenu_campagnes",
    category: "strategique",
    phases: ["startup", "scaleup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CMOB-011",
    name: "Strategie medias sociaux",
    description: "Politique et strategie de presence sur les reseaux sociaux.",
    botCode: "CMOB",
    blueprintSection: "canaux_budget",
    category: "strategique",
    phases: ["startup", "scaleup"],
    minTier: 1,
    docForgeReady: true,
    source: "existant",
  },

  // ── CEOB — Direction (13) ──
  {
    id: "TPL-CEOB-001",
    name: "Bilan annuel de la direction",
    description: "Revue annuelle des resultats, des realisations et des objectifs pour l'annee suivante.",
    botCode: "CEOB",
    blueprintSection: "objectifs_vitaa",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CEOB-002",
    name: "Charte de gouvernance d'entreprise",
    description: "Document fondateur definissant les regles de gouvernance, les roles du CA et les comites.",
    botCode: "CEOB",
    blueprintSection: "gouvernance",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CEOB-003",
    name: "Grille d'evaluation des opportunites (M&A)",
    description: "Grille structuree pour evaluer les opportunites d'acquisition ou de fusion.",
    botCode: "CEOB",
    blueprintSection: "risques_sortie",
    category: "operationnel",
    phases: ["exitup"],
    minTier: 4,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CEOB-004",
    name: "Matrice de priorisation des initiatives",
    description: "Outil pour prioriser les projets strategiques selon l'impact et l'effort.",
    botCode: "CEOB",
    blueprintSection: "objectifs_vitaa",
    category: "operationnel",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CEOB-005",
    name: "Memo decisionnel",
    description: "Document court pour formaliser une decision strategique importante.",
    botCode: "CEOB",
    blueprintSection: "gouvernance",
    category: "operationnel",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 1,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CEOB-006",
    name: "Plan de communication aux actionnaires",
    description: "Planification de la communication reguliere avec les investisseurs et actionnaires.",
    botCode: "CEOB",
    blueprintSection: "gouvernance",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CEOB-007",
    name: "Plan de releve du leadership",
    description: "Identification et preparation des successeurs pour les postes cles de direction.",
    botCode: "CEOB",
    blueprintSection: "risques_sortie",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CEOB-008",
    name: "Rapport d'avancement des chantiers",
    description: "Suivi mensuel de l'avancement des chantiers strategiques en cours.",
    botCode: "CEOB",
    blueprintSection: "kpis_direction",
    category: "operationnel",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CEOB-009",
    name: "Rapport au Conseil d'Administration",
    description: "Rapport trimestriel structure pour informer le CA de l'etat de l'entreprise.",
    botCode: "CEOB",
    blueprintSection: "gouvernance",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CEOB-010",
    name: "Tableau de bord du dirigeant",
    description: "Vue synthetique des KPI cles pour le CEO avec indicateurs de tendance.",
    botCode: "CEOB",
    blueprintSection: "kpis_direction",
    category: "operationnel",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 1,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CEOB-011",
    name: "Vision, mission et valeurs",
    description: "Document fondateur definissant la raison d'etre, la direction et les principes de l'entreprise.",
    botCode: "CEOB",
    blueprintSection: "profil",
    category: "strategique",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 1,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CEOB-012",
    name: "Plan de Releve et Transition Executive",
    description: "Plan detaille de releve pour les postes executifs incluant les jalons de transition.",
    botCode: "CEOB",
    blueprintSection: "risques_sortie",
    category: "strategique",
    phases: ["exitup"],
    minTier: 3,
    docForgeReady: false,
    source: "existant",
  },

  // ── CSOB — Strategie (12) ──
  {
    id: "TPL-CSOB-001",
    name: "Analyse de la chaine de valeur",
    description: "Deconstruction de la chaine de valeur pour identifier les avantages concurrentiels.",
    botCode: "CSOB",
    blueprintSection: "avantage_concurrentiel",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CSOB-002",
    name: "Analyse concurrentielle detaillee (5 Forces)",
    description: "Application du modele de Porter pour evaluer les forces concurrentielles du secteur.",
    botCode: "CSOB",
    blueprintSection: "concurrence",
    category: "operationnel",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CSOB-003",
    name: "Analyse PESTEL (Macro-environnement)",
    description: "Analyse des facteurs Politique, Economique, Social, Technologique, Environnemental, Legal.",
    botCode: "CSOB",
    blueprintSection: "pestel",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CSOB-004",
    name: "Etude de faisabilite (Nouveau marche/produit)",
    description: "Analyse approfondie de la viabilite d'un nouveau projet, marche ou produit.",
    botCode: "CSOB",
    blueprintSection: "diversification",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CSOB-005",
    name: "Evaluation de partenariat strategique",
    description: "Grille d'evaluation pour mesurer la pertinence d'un partenariat avant engagement.",
    botCode: "CSOB",
    blueprintSection: "partenariats",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CSOB-006",
    name: "Modele d'affaires (Business Model Canvas)",
    description: "Canevas visuel des 9 blocs du modele d'affaires selon Osterwalder.",
    botCode: "CSOB",
    blueprintSection: "marche",
    category: "operationnel",
    phases: ["startup", "scaleup"],
    minTier: 1,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CSOB-007",
    name: "Plan de croissance organique",
    description: "Strategie de croissance par developpement interne (nouveaux clients, upsell, penetration).",
    botCode: "CSOB",
    blueprintSection: "marche",
    category: "strategique",
    phases: ["scaleup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CSOB-008",
    name: "Plan de diversification",
    description: "Strategie pour entrer dans de nouveaux marches ou developper de nouveaux produits.",
    botCode: "CSOB",
    blueprintSection: "diversification",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CSOB-009",
    name: "Plan d'expansion geographique",
    description: "Feuille de route pour penetrer de nouveaux marches geographiques (provinces, pays).",
    botCode: "CSOB",
    blueprintSection: "diversification",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CSOB-010",
    name: "Scenarios strategiques (Horizon 5 ans)",
    description: "Modelisation de scenarios strategiques a long terme avec plans de contingence.",
    botCode: "CSOB",
    blueprintSection: "kpis_strategie",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CSOB-011",
    name: "Veille strategique (Rapport trimestriel)",
    description: "Synthese trimestrielle des tendances sectorielles et concurrentielles.",
    botCode: "CSOB",
    blueprintSection: "pestel",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CSOB-012",
    name: "Matrice de Transition et Reporting ESG (Alignement BNQ 9700-700)",
    description: "Tableau de bord ESG aligne sur la norme BNQ 9700-700 pour PME quebecoises.",
    botCode: "CSOB",
    blueprintSection: "kpis_strategie",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: false,
    source: "existant",
  },

  // ── CTOB — Technologie (12) ──
  {
    id: "TPL-CTOB-001",
    name: "Audit de maturite numerique",
    description: "Evaluation complete du niveau de maturite numerique de l'entreprise.",
    botCode: "CTOB",
    blueprintSection: "stack_technique",
    category: "diagnostic",
    phases: ["startup", "scaleup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CTOB-002",
    name: "Bilan de la dette technique",
    description: "Inventaire et priorisation de la dette technique avec plan de remediation.",
    botCode: "CTOB",
    blueprintSection: "dette_technique",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CTOB-003",
    name: "Cahier des charges logiciel (RFP)",
    description: "Document detaille pour lancer un appel d'offres ou un projet de developpement.",
    botCode: "CTOB",
    blueprintSection: "roadmap_tech",
    category: "operationnel",
    phases: ["scaleup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CTOB-004",
    name: "Feuille de route technologique (Roadmap TI)",
    description: "Planification des initiatives technologiques sur 12-36 mois.",
    botCode: "CTOB",
    blueprintSection: "roadmap_tech",
    category: "strategique",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CTOB-005",
    name: "Evaluation de solutions technologiques",
    description: "Grille d'evaluation multicritere pour comparer des solutions TI.",
    botCode: "CTOB",
    blueprintSection: "architecture_si",
    category: "operationnel",
    phases: ["startup", "scaleup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CTOB-006",
    name: "Guide d'architecture de donnees",
    description: "Documentation de l'architecture de donnees, des flux et des normes de qualite.",
    botCode: "CTOB",
    blueprintSection: "architecture_si",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CTOB-007",
    name: "Plan de continuite informatique (PCI)",
    description: "Plan de continuite des systemes informatiques en cas de sinistre.",
    botCode: "CTOB",
    blueprintSection: "infrastructure",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CTOB-008",
    name: "Plan d'integration systeme (API)",
    description: "Plan detaille pour integrer des systemes via APIs ou middleware.",
    botCode: "CTOB",
    blueprintSection: "architecture_si",
    category: "operationnel",
    phases: ["scaleup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CTOB-009",
    name: "Plan de migration cloud",
    description: "Strategie et plan d'execution pour migrer les systemes vers le cloud.",
    botCode: "CTOB",
    blueprintSection: "infrastructure",
    category: "strategique",
    phases: ["scaleup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CTOB-010",
    name: "Politique de gestion des donnees",
    description: "Politique encadrant la collecte, le stockage et le cycle de vie des donnees.",
    botCode: "CTOB",
    blueprintSection: "securite_applicative",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CTOB-011",
    name: "Rapport d'incident majeur (Post-Mortem)",
    description: "Analyse post-incident avec causes fondamentales et actions correctives.",
    botCode: "CTOB",
    blueprintSection: "kpis_tech",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CTOB-012",
    name: "Diagnostic de Maturite Numerique et Plan d'Intervention (Industrie 4.0)",
    description: "Diagnostic approfondi pour les entreprises manufacturieres en route vers l'Industrie 4.0.",
    botCode: "CTOB",
    blueprintSection: "stack_technique",
    category: "diagnostic",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: false,
    source: "existant",
  },

  // ── CPOB — Production & Produit (12) ──
  {
    id: "TPL-CPOB-001",
    name: "Analyse TRS / OEE",
    description: "Analyse du Taux de Rendement Synthetique pour identifier les pertes de productivite.",
    botCode: "CPOB",
    blueprintSection: "kpis_production",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CPOB-002",
    name: "Grille d'audit 5S",
    description: "Formulaire standardise pour auditer les zones de travail selon la methode 5S.",
    botCode: "CPOB",
    blueprintSection: "qualite",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CPOB-003",
    name: "Audit SST (Sante et Securite au Travail)",
    description: "Grille d'audit pour evaluer la conformite SST en milieu de production.",
    botCode: "CPOB",
    blueprintSection: "sst",
    category: "diagnostic",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CPOB-004",
    name: "Plan d'amelioration de ligne de production",
    description: "Plan detaille pour ameliorer la performance d'une ligne de production specifique.",
    botCode: "CPOB",
    blueprintSection: "planification_production",
    category: "strategique",
    phases: ["scaleup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CPOB-005",
    name: "Plan d'amenagement d'usine (Layout)",
    description: "Plan physique d'amenagement ou de reamenagement des zones de production.",
    botCode: "CPOB",
    blueprintSection: "planification_production",
    category: "strategique",
    phases: ["scaleup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CPOB-006",
    name: "Plan CAPEX equipement",
    description: "Planification des investissements en equipements de production.",
    botCode: "CPOB",
    blueprintSection: "equipements",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CPOB-007",
    name: "Plan de maintenance preventive/TPM",
    description: "Programme de maintenance preventive et de Total Productive Maintenance.",
    botCode: "CPOB",
    blueprintSection: "equipements",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CPOB-008",
    name: "Plan de production mensuel",
    description: "Planification mensuelle de la production avec ordonnancement des commandes.",
    botCode: "CPOB",
    blueprintSection: "planification_production",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CPOB-009",
    name: "Procedure standard d'operation (SOP)",
    description: "Documentation standardisee des procedures operationnelles critiques.",
    botCode: "CPOB",
    blueprintSection: "qualite",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CPOB-010",
    name: "Rapport d'incident de production",
    description: "Rapport detaille d'un incident de production avec analyse des causes.",
    botCode: "CPOB",
    blueprintSection: "sst",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CPOB-011",
    name: "Rapport qualite mensuel",
    description: "Suivi mensuel des indicateurs qualite, des non-conformites et des actions correctives.",
    botCode: "CPOB",
    blueprintSection: "qualite",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CPOB-012",
    name: "Registre Unifie de Conformite Plancher (CNESST & MELCCFP)",
    description: "Registre quotidien de conformite aux normes CNESST et environnementales pour le plancher.",
    botCode: "CPOB",
    blueprintSection: "sst",
    category: "conformite",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: false,
    source: "existant",
  },

  // ── CHROB — Ressources Humaines (12) ──
  {
    id: "TPL-CHROB-001",
    name: "Guide de la convention collective",
    description: "Document de reference sur les clauses cles de la convention collective.",
    botCode: "CHROB",
    blueprintSection: "conformite_rh",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CHROB-002",
    name: "Formulaire d'evaluation de performance",
    description: "Grille d'evaluation annuelle des employes avec objectifs et competences.",
    botCode: "CHROB",
    blueprintSection: "culture_engagement",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CHROB-003",
    name: "Grille salariale et avantages sociaux",
    description: "Structure salariale par poste avec echelles et avantages offerts.",
    botCode: "CHROB",
    blueprintSection: "remuneration",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CHROB-004",
    name: "Plan de formation annuel",
    description: "Planification des formations par departement avec budget et objectifs.",
    botCode: "CHROB",
    blueprintSection: "formation",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CHROB-005",
    name: "Plan d'integration (onboarding)",
    description: "Programme d'accueil et d'integration pour les nouveaux employes.",
    botCode: "CHROB",
    blueprintSection: "recrutement",
    category: "strategique",
    phases: ["scaleup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CHROB-006",
    name: "Plan de releve des postes cles",
    description: "Identification des postes critiques et des successeurs potentiels.",
    botCode: "CHROB",
    blueprintSection: "succession",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CHROB-007",
    name: "Politique de recrutement",
    description: "Politique encadrant le processus de recrutement et de selection.",
    botCode: "CHROB",
    blueprintSection: "recrutement",
    category: "strategique",
    phases: ["scaleup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CHROB-008",
    name: "Politique SST entreprise",
    description: "Politique de sante et securite au travail conforme a la CNESST.",
    botCode: "CHROB",
    blueprintSection: "conformite_rh",
    category: "conformite",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CHROB-009",
    name: "Politique de teletravail hybride",
    description: "Politique definissant les regles du travail a distance et hybride.",
    botCode: "CHROB",
    blueprintSection: "culture_engagement",
    category: "strategique",
    phases: ["scaleup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CHROB-010",
    name: "Rapport mensuel des indicateurs RH",
    description: "Tableau de bord mensuel des KPI RH (turnover, absenteisme, recrutement).",
    botCode: "CHROB",
    blueprintSection: "kpis_rh",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CHROB-011",
    name: "Sondage d'engagement employe",
    description: "Questionnaire structure pour mesurer l'engagement et la satisfaction des employes.",
    botCode: "CHROB",
    blueprintSection: "culture_engagement",
    category: "operationnel",
    phases: ["scaleup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CHROB-012",
    name: "Dossier de Conformite Sociale (LNT) et Developpement des Competences",
    description: "Documentation de conformite aux normes du travail et suivi de la Loi 1% sur la formation.",
    botCode: "CHROB",
    blueprintSection: "conformite_rh",
    category: "conformite",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: false,
    source: "existant",
  },

  // ── CINOB — Innovation & R&D (11) ──
  {
    id: "TPL-CINOB-001",
    name: "Analyse Make or Buy",
    description: "Analyse comparative pour decider de fabriquer ou d'acheter un composant/service.",
    botCode: "CINOB",
    blueprintSection: "pipeline_innovation",
    category: "operationnel",
    phases: ["scaleup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CINOB-002",
    name: "Business case d'innovation",
    description: "Document justificatif pour un projet d'innovation avec ROI et risques.",
    botCode: "CINOB",
    blueprintSection: "pipeline_innovation",
    category: "operationnel",
    phases: ["startup", "scaleup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CINOB-003",
    name: "Cahier de projet d'innovation",
    description: "Documentation detaillee d'un projet de R&D avec hypotheses et jalons.",
    botCode: "CINOB",
    blueprintSection: "rsde",
    category: "operationnel",
    phases: ["startup", "scaleup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CINOB-004",
    name: "Dossier RS&DE (credits d'impot)",
    description: "Dossier technique et financier pour la reclamation des credits RS&DE.",
    botCode: "CINOB",
    blueprintSection: "rsde",
    category: "conformite",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CINOB-005",
    name: "Fiche technique nouveau produit",
    description: "Fiche descriptive d'un nouveau produit avec specifications techniques.",
    botCode: "CINOB",
    blueprintSection: "prototypage",
    category: "operationnel",
    phases: ["startup", "scaleup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CINOB-006",
    name: "Plan de prototypage rapide",
    description: "Plan structure pour le developpement rapide de prototypes avec iterations.",
    botCode: "CINOB",
    blueprintSection: "prototypage",
    category: "strategique",
    phases: ["startup", "scaleup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CINOB-007",
    name: "Plan de transfert technologique",
    description: "Plan pour transferer une technologie entre equipes ou vers un partenaire.",
    botCode: "CINOB",
    blueprintSection: "partenariats_rd",
    category: "strategique",
    phases: ["scaleup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CINOB-008",
    name: "Registre de propriete intellectuelle",
    description: "Inventaire des brevets, marques et secrets commerciaux de l'entreprise.",
    botCode: "CINOB",
    blueprintSection: "propriete_intellectuelle",
    category: "conformite",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CINOB-009",
    name: "Rapport d'essais et de tests",
    description: "Documentation structuree des resultats d'essais et de tests techniques.",
    botCode: "CINOB",
    blueprintSection: "prototypage",
    category: "operationnel",
    phases: ["startup", "scaleup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CINOB-010",
    name: "Roadmap produit/innovation",
    description: "Feuille de route des projets d'innovation avec jalons et ressources.",
    botCode: "CINOB",
    blueprintSection: "pipeline_innovation",
    category: "strategique",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CINOB-011",
    name: "Rapport de veille technologique",
    description: "Synthese periodique des tendances technologiques et de leur impact potentiel.",
    botCode: "CINOB",
    blueprintSection: "veille",
    category: "operationnel",
    phases: ["scaleup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },

  // ── CLOB — Legal (12) ──
  {
    id: "TPL-CLOB-001",
    name: "Conditions generales de vente (CGV)",
    description: "Conditions contractuelles standard applicables aux ventes de produits ou services.",
    botCode: "CLOB",
    blueprintSection: "contrats",
    category: "conformite",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 1,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CLOB-002",
    name: "Contrat d'emploi standard",
    description: "Modele de contrat de travail conforme aux normes du travail du Quebec.",
    botCode: "CLOB",
    blueprintSection: "contrats",
    category: "conformite",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 1,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CLOB-003",
    name: "Contrat-cadre fournisseur",
    description: "Modele de contrat pour encadrer les relations avec les fournisseurs recurrents.",
    botCode: "CLOB",
    blueprintSection: "contrats",
    category: "conformite",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CLOB-004",
    name: "Evaluation des facteurs relatifs a la vie privee (EFVP)",
    description: "Analyse d'impact requise par la Loi 25 avant tout transfert de donnees personnelles.",
    botCode: "CLOB",
    blueprintSection: "conformite_loi25",
    category: "conformite",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CLOB-005",
    name: "Entente de non-divulgation (NDA)",
    description: "Accord de confidentialite standardise pour proteger les informations sensibles.",
    botCode: "CLOB",
    blueprintSection: "contrats",
    category: "conformite",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 1,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CLOB-006",
    name: "Manuel de gouvernance d'entreprise",
    description: "Manuel decrivant la structure de gouvernance, les roles et les processus decisionnels.",
    botCode: "CLOB",
    blueprintSection: "structure_corporative",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CLOB-007",
    name: "Politique anti-corruption et cadeaux",
    description: "Politique encadrant les pratiques anti-corruption et les conflits d'interets.",
    botCode: "CLOB",
    blueprintSection: "reglementaire",
    category: "conformite",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CLOB-008",
    name: "Politique de confidentialite (Loi 25)",
    description: "Politique de protection des renseignements personnels conforme a la Loi 25.",
    botCode: "CLOB",
    blueprintSection: "conformite_loi25",
    category: "conformite",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 1,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CLOB-009",
    name: "Registre des consentements (Loi 25)",
    description: "Registre tracant les consentements obtenus pour la collecte de donnees personnelles.",
    botCode: "CLOB",
    blueprintSection: "conformite_loi25",
    category: "conformite",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 1,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CLOB-010",
    name: "Registre des incidents de donnees",
    description: "Registre documentant les incidents de confidentialite et les mesures prises.",
    botCode: "CLOB",
    blueprintSection: "conformite_loi25",
    category: "conformite",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 1,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CLOB-011",
    name: "Registre des plaintes et litiges",
    description: "Suivi centralise des plaintes, reclamations et litiges en cours.",
    botCode: "CLOB",
    blueprintSection: "litiges",
    category: "conformite",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CLOB-012",
    name: "Infrastructure de Gouvernance des Donnees et Registres (Loi 25)",
    description: "Structure complete de gouvernance des donnees incluant tous les registres requis par la Loi 25.",
    botCode: "CLOB",
    blueprintSection: "conformite_loi25",
    category: "conformite",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: false,
    source: "existant",
  },

  // ── COOB — Operations (12) ──
  {
    id: "TPL-COOB-001",
    name: "Analyse des goulots d'etranglement (Bottleneck)",
    description: "Identification et analyse des goulots dans les processus operationnels.",
    botCode: "COOB",
    blueprintSection: "processus",
    category: "operationnel",
    phases: ["scaleup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-COOB-002",
    name: "Audit de performance operationnelle",
    description: "Evaluation complete de la performance des processus operationnels.",
    botCode: "COOB",
    blueprintSection: "kpis_operations",
    category: "diagnostic",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-COOB-003",
    name: "Grille d'evaluation des fournisseurs",
    description: "Grille multicritere pour evaluer et comparer les fournisseurs existants.",
    botCode: "COOB",
    blueprintSection: "gestion_fournisseurs",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-COOB-004",
    name: "Manuel qualite (Inspiration ISO 9001)",
    description: "Manuel qualite decrivant le systeme de gestion de la qualite de l'entreprise.",
    botCode: "COOB",
    blueprintSection: "amelioration_continue",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-COOB-005",
    name: "Plan d'amelioration continue",
    description: "Plan formalisant les initiatives d'amelioration continue (Kaizen, Lean).",
    botCode: "COOB",
    blueprintSection: "amelioration_continue",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-COOB-006",
    name: "Plan de capacite et planification de production",
    description: "Analyse de la capacite de production avec plan d'ordonnancement.",
    botCode: "COOB",
    blueprintSection: "logistique",
    category: "strategique",
    phases: ["scaleup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-COOB-007",
    name: "Plan de gestion des stocks et inventaires",
    description: "Politique et plan de gestion optimale des stocks (MP, EC, PF).",
    botCode: "COOB",
    blueprintSection: "logistique",
    category: "strategique",
    phases: ["scaleup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-COOB-008",
    name: "Plan de reduction des couts operationnels",
    description: "Identification et plan d'execution de reduction des couts d'exploitation.",
    botCode: "COOB",
    blueprintSection: "kpis_operations",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-COOB-009",
    name: "Plan de gestion de la supply chain",
    description: "Strategie d'approvisionnement avec gestion des risques et alternatives.",
    botCode: "COOB",
    blueprintSection: "supply_chain",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-COOB-010",
    name: "Procedure Operationnelle Standard (SOP)",
    description: "Documentation standardisee des procedures operationnelles de l'entreprise.",
    botCode: "COOB",
    blueprintSection: "processus",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-COOB-011",
    name: "Rapport KPI Operations (Mensuel)",
    description: "Rapport mensuel des indicateurs de performance operationnelle.",
    botCode: "COOB",
    blueprintSection: "kpis_operations",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-COOB-012",
    name: "Audit Qualite et Gestion des Risques de la Chaine d'Approvisionnement",
    description: "Audit semestriel de la qualite et des risques dans la chaine d'approvisionnement.",
    botCode: "COOB",
    blueprintSection: "supply_chain",
    category: "diagnostic",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: false,
    source: "existant",
  },

  // ── CROB — Ventes & Revenus (11) ──
  {
    id: "TPL-CROB-001",
    name: "Analyse Win/Loss",
    description: "Analyse systematique des opportunites gagnees et perdues pour ameliorer le processus.",
    botCode: "CROB",
    blueprintSection: "pipeline_funnel",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CROB-002",
    name: "Fiche d'analyse concurrentielle",
    description: "Fiche synthetique d'analyse d'un concurrent specifique.",
    botCode: "CROB",
    blueprintSection: "methodologie_vente",
    category: "operationnel",
    phases: ["startup", "scaleup"],
    minTier: 1,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CROB-003",
    name: "Structure de commissions",
    description: "Definition des structures de remuneration variable pour l'equipe de vente.",
    botCode: "CROB",
    blueprintSection: "remuneration_ventes",
    category: "strategique",
    phases: ["scaleup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CROB-004",
    name: "Grille de qualification prospect (BANT/MEDDIC)",
    description: "Formulaire standardise pour qualifier les opportunites selon BANT ou MEDDIC.",
    botCode: "CROB",
    blueprintSection: "methodologie_vente",
    category: "operationnel",
    phases: ["startup", "scaleup"],
    minTier: 1,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CROB-005",
    name: "Plan de compte strategique",
    description: "Plan detaille pour developper la relation avec un client strategique.",
    botCode: "CROB",
    blueprintSection: "comptes_cles",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CROB-006",
    name: "Plan de developpement de territoire",
    description: "Strategie de penetration et de developpement d'un territoire de vente.",
    botCode: "CROB",
    blueprintSection: "territoires",
    category: "strategique",
    phases: ["scaleup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CROB-007",
    name: "Playbook de vente",
    description: "Manuel complet du processus de vente avec scripts, objections et bonnes pratiques.",
    botCode: "CROB",
    blueprintSection: "methodologie_vente",
    category: "strategique",
    phases: ["startup", "scaleup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CROB-008",
    name: "Rapport de previsions de ventes",
    description: "Previsions de ventes mensuelles basees sur le pipeline et les tendances.",
    botCode: "CROB",
    blueprintSection: "kpis_ventes",
    category: "operationnel",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CROB-009",
    name: "Modele de proposition commerciale",
    description: "Template de proposition commerciale professionnel et personnalisable.",
    botCode: "CROB",
    blueprintSection: "pipeline_funnel",
    category: "operationnel",
    phases: ["startup", "scaleup"],
    minTier: 1,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CROB-010",
    name: "Rapport d'activite commerciale hebdo",
    description: "Suivi hebdomadaire des activites de l'equipe de vente (appels, rdv, demos).",
    botCode: "CROB",
    blueprintSection: "kpis_ventes",
    category: "operationnel",
    phases: ["scaleup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CROB-011",
    name: "Script de prospection telephonique",
    description: "Script structure pour les appels de prospection avec variantes et objections.",
    botCode: "CROB",
    blueprintSection: "methodologie_vente",
    category: "operationnel",
    phases: ["startup", "scaleup"],
    minTier: 1,
    docForgeReady: true,
    source: "existant",
  },

  // ── CISOB — Securite (12) ──
  {
    id: "TPL-CISOB-001",
    name: "Rapport d'audit de vulnerabilites",
    description: "Rapport documentant les vulnerabilites identifiees lors d'un scan de securite.",
    botCode: "CISOB",
    blueprintSection: "vulnerabilites",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CISOB-002",
    name: "Questionnaire de securite fournisseur",
    description: "Evaluation de la posture de securite des fournisseurs tiers.",
    botCode: "CISOB",
    blueprintSection: "vulnerabilites",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CISOB-003",
    name: "Matrice des risques organisationnels",
    description: "Cartographie des risques organisationnels avec probabilite et impact.",
    botCode: "CISOB",
    blueprintSection: "incidents_reponse",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CISOB-004",
    name: "Plan de continuite des affaires (PCA)",
    description: "Plan assurant la continuite des activites critiques en cas de sinistre majeur.",
    botCode: "CISOB",
    blueprintSection: "sauvegardes",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CISOB-005",
    name: "Plan de reponse aux incidents de securite",
    description: "Procedure detaillee de reponse aux incidents de cybersecurite.",
    botCode: "CISOB",
    blueprintSection: "incidents_reponse",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CISOB-006",
    name: "Plan de sensibilisation a la securite",
    description: "Programme de formation et de sensibilisation a la cybersecurite pour les employes.",
    botCode: "CISOB",
    blueprintSection: "formation_phishing",
    category: "strategique",
    phases: ["scaleup"],
    minTier: 2,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CISOB-007",
    name: "Politique de sauvegarde et restauration",
    description: "Politique definissant la strategie de backup, les frequences et les tests de restauration.",
    botCode: "CISOB",
    blueprintSection: "sauvegardes",
    category: "strategique",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 1,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CISOB-008",
    name: "Politique de cybersecurite",
    description: "Politique globale encadrant les pratiques de securite informatique de l'entreprise.",
    botCode: "CISOB",
    blueprintSection: "politiques_iam",
    category: "strategique",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 1,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CISOB-009",
    name: "Plan de gestion de crise",
    description: "Plan de gestion de crise incluant communication et coordination.",
    botCode: "CISOB",
    blueprintSection: "incidents_reponse",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CISOB-010",
    name: "Rapport de securite mensuel",
    description: "Rapport mensuel de l'etat de la securite avec metriques et incidents.",
    botCode: "CISOB",
    blueprintSection: "kpis_securite",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CISOB-011",
    name: "Registre des acces et permissions",
    description: "Inventaire centralise des acces utilisateurs et des permissions systemes.",
    botCode: "CISOB",
    blueprintSection: "politiques_iam",
    category: "conformite",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 1,
    docForgeReady: true,
    source: "existant",
  },
  {
    id: "TPL-CISOB-012",
    name: "Politique de Cybersecurite des Technologies Operationnelles (OT)",
    description: "Politique specifique pour securiser les systemes industriels et SCADA.",
    botCode: "CISOB",
    blueprintSection: "politiques_iam",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: false,
    source: "existant",
  },
];

// ── Templates nouveaux identifies par DeepSearch (68) ──

const NEW_TEMPLATES: BlueprintTemplate[] = [
  // ── CEOB — Direction (4 nouveaux) ──
  {
    id: "TPL-CEOB-N01",
    name: "Fiche identite entreprise",
    description: "Fiche fondamentale regroupant le nom, secteur, taille, CA et coordonnees de l'entreprise.",
    botCode: "CEOB",
    blueprintSection: "profil",
    category: "conformite",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 1,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CEOB-N02",
    name: "Business Model Canvas (BMC) Strategique",
    description: "Canevas strategique des 9 blocs (valeur, operations, viabilite) integre au Blueprint.",
    botCode: "CEOB",
    blueprintSection: "bmc",
    category: "strategique",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 1,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CEOB-N03",
    name: "Matrice SWOT Dynamique",
    description: "Analyse SWOT evolutive avec croisement forces-opportunites et plan d'action priorise.",
    botCode: "CEOB",
    blueprintSection: "swot",
    category: "operationnel",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CEOB-N04",
    name: "Plan de Sortie et Scenarios",
    description: "Document structurant la strategie de sortie avec valorisation cible et scenarios de liquidite.",
    botCode: "CEOB",
    blueprintSection: "risques_sortie",
    category: "strategique",
    phases: ["exitup"],
    minTier: 4,
    docForgeReady: true,
    source: "nouveau",
  },

  // ── CTOB — Technologie (4 nouveaux) ──
  {
    id: "TPL-CTOB-N01",
    name: "Registre des Actifs et Licences IT",
    description: "Inventaire exhaustif des systemes, licences SaaS, couts et matrice d'integration.",
    botCode: "CTOB",
    blueprintSection: "stack_technique",
    category: "conformite",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CTOB-N02",
    name: "Plan de Remediation de la Dette Technique",
    description: "Plan structure avec evaluation de l'impact, budget et priorisation de la remediation.",
    botCode: "CTOB",
    blueprintSection: "dette_technique",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CTOB-N03",
    name: "Roadmap Technologique (Feuille de route 12-36 mois)",
    description: "Planification des projets technologiques a moyen terme avec jalons et dependances.",
    botCode: "CTOB",
    blueprintSection: "roadmap_tech",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CTOB-N04",
    name: "Guide de Developpement Securise (OWASP)",
    description: "Guide des pratiques de developpement securise basees sur les standards OWASP.",
    botCode: "CTOB",
    blueprintSection: "securite_applicative",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: false,
    source: "nouveau",
  },

  // ── CFOB — Finance (4 nouveaux) ──
  {
    id: "TPL-CFOB-N01",
    name: "Memorandum d'Information Confidentiel (CIM)",
    description: "Document complet de valorisation pour structurer les sorties ou levees de fonds (EBITDA, multiples).",
    botCode: "CFOB",
    blueprintSection: "valorisation",
    category: "strategique",
    phases: ["exitup"],
    minTier: 4,
    docForgeReady: false,
    source: "nouveau",
  },
  {
    id: "TPL-CFOB-N02",
    name: "Plan de Gestion du BFR",
    description: "Plan de gestion du Besoin en Fonds de Roulement avec indicateurs CCC, DSO et DPO.",
    botCode: "CFOB",
    blueprintSection: "tresorerie",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CFOB-N03",
    name: "Modele de Revenus et Mix Produit",
    description: "Analyse des sources de revenus, mix produit et marges pour optimiser la strategie de prix.",
    botCode: "CFOB",
    blueprintSection: "modele_revenus",
    category: "operationnel",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 1,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CFOB-N04",
    name: "Dossier Financier Credits & Subventions",
    description: "Dossier compile pour reclamer les credits RS&DE, CRIC et subventions (IQ, ESSOR).",
    botCode: "CFOB",
    blueprintSection: "fiscalite",
    category: "conformite",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: false,
    source: "nouveau",
  },

  // ── CMOB — Marketing (4 nouveaux) ──
  {
    id: "TPL-CMOB-N01",
    name: "Profil Client Ideal (ICP) & Empathy Map",
    description: "Definition approfondie de l'ICP avec carte d'empathie et parcours d'achat.",
    botCode: "CMOB",
    blueprintSection: "personas_icp",
    category: "strategique",
    phases: ["startup", "scaleup"],
    minTier: 1,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CMOB-N02",
    name: "Audit de l'Ecosysteme MarTech",
    description: "Inventaire et evaluation de l'ecosysteme technologique marketing (outils, integrations, ROI).",
    botCode: "CMOB",
    blueprintSection: "automatisation",
    category: "diagnostic",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CMOB-N03",
    name: "Plan de Relations Publiques & Gestion de Crise",
    description: "Plan de gestion de la reputation et protocole de communication en cas de crise mediatique.",
    botCode: "CMOB",
    blueprintSection: "reputation",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CMOB-N04",
    name: "Charte de Positionnement (Brand Book)",
    description: "Document fondateur definissant l'identite de marque, le ton de voix et le positionnement.",
    botCode: "CMOB",
    blueprintSection: "positionnement",
    category: "strategique",
    phases: ["startup", "scaleup"],
    minTier: 2,
    docForgeReady: true,
    source: "nouveau",
  },

  // ── CSOB — Strategie (5 nouveaux) ──
  {
    id: "TPL-CSOB-N01",
    name: "Analyse Macro-environnementale (PESTEL)",
    description: "Analyse structuree des facteurs Politique, Economique, Social, Technologique, Environnemental, Legal.",
    botCode: "CSOB",
    blueprintSection: "pestel",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CSOB-N02",
    name: "Dimensionnement du Marche (TAM/SAM/SOM)",
    description: "Analyse quantifiee du marche total, accessible et obtainable avec projections.",
    botCode: "CSOB",
    blueprintSection: "marche",
    category: "operationnel",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CSOB-N03",
    name: "Cartographie Concurrentielle (5 Forces Porter)",
    description: "Analyse detaillee des forces concurrentielles selon le modele de Porter.",
    botCode: "CSOB",
    blueprintSection: "concurrence",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CSOB-N04",
    name: "Matrice de Diversification (Ansoff)",
    description: "Application de la matrice Ansoff pour evaluer les strategies de croissance.",
    botCode: "CSOB",
    blueprintSection: "diversification",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CSOB-N05",
    name: "Registre des Partenariats Strategiques",
    description: "Registre centralise des partenariats avec evaluation de performance et renouvellements.",
    botCode: "CSOB",
    blueprintSection: "partenariats",
    category: "conformite",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },

  // ── COOB — Operations (4 nouveaux) ──
  {
    id: "TPL-COOB-N01",
    name: "Cartographie des Processus (SIPOC)",
    description: "Cartographie SIPOC des processus cles (Supplier, Input, Process, Output, Customer).",
    botCode: "COOB",
    blueprintSection: "processus",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-COOB-N02",
    name: "Matrice de Resolution de Problemes (Rapport A3)",
    description: "Rapport A3 Lean pour la resolution structuree de problemes (PDCA, 5 Pourquoi, Ishikawa).",
    botCode: "COOB",
    blueprintSection: "amelioration_continue",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-COOB-N03",
    name: "Plan de Continuite de la Chaine d'Approvisionnement",
    description: "Plan de contingence pour assurer la continuite de l'approvisionnement face aux risques.",
    botCode: "COOB",
    blueprintSection: "supply_chain",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-COOB-N04",
    name: "Plan de Capacite et Ordonnancement (MRP)",
    description: "Planification des capacites et ordonnancement de la production avec analyse de charge.",
    botCode: "COOB",
    blueprintSection: "logistique",
    category: "operationnel",
    phases: ["scaleup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },

  // ── CPOB — Production (4 nouveaux) ──
  {
    id: "TPL-CPOB-N01",
    name: "Programme de Prevention Exhaustif (CNESST)",
    description: "Programme complet conforme a la CNESST incluant risques, EPI, comite SST et formation.",
    botCode: "CPOB",
    blueprintSection: "sst",
    category: "conformite",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: false,
    source: "nouveau",
  },
  {
    id: "TPL-CPOB-N02",
    name: "Registre des Non-Conformites et CAPA (ISO 9001)",
    description: "Registre structure des actions correctives et preventives pour la gestion qualite.",
    botCode: "CPOB",
    blueprintSection: "qualite",
    category: "conformite",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CPOB-N03",
    name: "Matrice d'Analyse des Modes de Defaillance (AMDEC)",
    description: "Analyse AMDEC pour identifier et prioriser les modes de defaillance des equipements.",
    botCode: "CPOB",
    blueprintSection: "equipements",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CPOB-N04",
    name: "Plan de Preparation Audit de Certification",
    description: "Check-list et plan detaille pour preparer un audit de certification (ISO 9001, IATF).",
    botCode: "CPOB",
    blueprintSection: "normes_certifications",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 4,
    docForgeReady: true,
    source: "nouveau",
  },

  // ── CHROB — RH (4 nouveaux) ──
  {
    id: "TPL-CHROB-N01",
    name: "Dossier d'Equite Salariale et Declaration (DEMES)",
    description: "Dossier complet d'exercice d'equite salariale conforme a la LES du Quebec (10+ employes).",
    botCode: "CHROB",
    blueprintSection: "conformite_rh",
    category: "conformite",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: false,
    source: "nouveau",
  },
  {
    id: "TPL-CHROB-N02",
    name: "Registre des Depenses de Formation (Loi 1%)",
    description: "Registre de suivi des depenses de formation pour conformite a la Loi favorisant le developpement des competences.",
    botCode: "CHROB",
    blueprintSection: "formation",
    category: "conformite",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CHROB-N03",
    name: "Organigramme et Planification des Effectifs",
    description: "Organigramme formel avec plan de dotation et prevision des besoins en main-d'oeuvre.",
    botCode: "CHROB",
    blueprintSection: "organigramme",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CHROB-N04",
    name: "Guide de Conformite aux Normes du Travail",
    description: "Guide complet des obligations legales de l'employeur selon la LNT du Quebec.",
    botCode: "CHROB",
    blueprintSection: "conformite_rh",
    category: "conformite",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },

  // ── CINOB — Innovation (3 nouveaux) ──
  {
    id: "TPL-CINOB-N01",
    name: "Fiche de Documentation Technique (RS&DE)",
    description: "Documentation contemporaine des projets R&D conforme aux exigences RS&DE (incertitude, hypotheses, progression).",
    botCode: "CINOB",
    blueprintSection: "rsde",
    category: "conformite",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: false,
    source: "nouveau",
  },
  {
    id: "TPL-CINOB-N02",
    name: "Tableau de Bord du Portefeuille R&D",
    description: "Registre consolide des projets R&D avec phases, budgets et indicateurs de progression.",
    botCode: "CINOB",
    blueprintSection: "pipeline_innovation",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CINOB-N03",
    name: "Convention de Recherche Collaborative",
    description: "Modele de convention pour encadrer les partenariats de recherche (universites, MITACS).",
    botCode: "CINOB",
    blueprintSection: "partenariats_rd",
    category: "conformite",
    phases: ["scaleup"],
    minTier: 3,
    docForgeReady: false,
    source: "nouveau",
  },

  // ── CROB — Ventes (4 nouveaux) ──
  {
    id: "TPL-CROB-N01",
    name: "Playbook de Vente B2B Structuree (MEDDIC)",
    description: "Manuel complet du processus de vente B2B avec framework MEDDIC, scripts et gestion des objections.",
    botCode: "CROB",
    blueprintSection: "methodologie_vente",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CROB-N02",
    name: "Plan de Remuneration Variable, Commissions et Quotas",
    description: "Structure detaillee des commissions, OTE et quotas par vendeur et par territoire.",
    botCode: "CROB",
    blueprintSection: "remuneration_ventes",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CROB-N03",
    name: "Plan Strategique de Compte Cle (Key Account Plan)",
    description: "Plan detaille pour developper et retenir les comptes strategiques les plus importants.",
    botCode: "CROB",
    blueprintSection: "comptes_cles",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CROB-N04",
    name: "Programme d'Onboarding et Formation aux Ventes",
    description: "Programme structure d'integration et de formation continue pour l'equipe de vente.",
    botCode: "CROB",
    blueprintSection: "methodologie_vente",
    category: "strategique",
    phases: ["scaleup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },

  // ── CLOB — Legal (4 nouveaux) ──
  {
    id: "TPL-CLOB-N01",
    name: "Registre des Incidents de Confidentialite (Loi 25)",
    description: "Registre obligatoire des incidents de confidentialite avec evaluation du risque de prejudice.",
    botCode: "CLOB",
    blueprintSection: "conformite_loi25",
    category: "conformite",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 1,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CLOB-N02",
    name: "Evaluation des Facteurs relatifs a la Vie Privee (EFVP) — Approfondie",
    description: "Analyse d'impact approfondie avant tout transfert de donnees hors Quebec, conforme a la Loi 25.",
    botCode: "CLOB",
    blueprintSection: "conformite_loi25",
    category: "conformite",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CLOB-N03",
    name: "Registre des Administrateurs et Resolutions",
    description: "Registre centralise des administrateurs, resolutions du CA et minutes corporatives.",
    botCode: "CLOB",
    blueprintSection: "structure_corporative",
    category: "conformite",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CLOB-N04",
    name: "Calendrier de Renouvellements Reglementaires",
    description: "Calendrier de suivi des echeances reglementaires, permis et licences a renouveler.",
    botCode: "CLOB",
    blueprintSection: "reglementaire",
    category: "conformite",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "nouveau",
  },

  // ── CISOB — Securite (4 nouveaux) ──
  {
    id: "TPL-CISOB-N01",
    name: "Politique de Gestion des Identites et Acces (IAM)",
    description: "Politique detaillee encadrant l'authentification MFA, le moindre privilege et le RBAC.",
    botCode: "CISOB",
    blueprintSection: "politiques_iam",
    category: "strategique",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CISOB-N02",
    name: "Plan de Reprise d'Activite Informatique (PRA / DRP)",
    description: "Plan detaille de reprise apres sinistre avec RTO/RPO, strategie de sauvegarde et procedure de restauration.",
    botCode: "CISOB",
    blueprintSection: "sauvegardes",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: false,
    source: "nouveau",
  },
  {
    id: "TPL-CISOB-N03",
    name: "Declaration d'Applicabilite (SoA) — ISO 27001",
    description: "Matrice de declaration d'applicabilite alignee sur l'Annexe A de la norme ISO 27001.",
    botCode: "CISOB",
    blueprintSection: "certifications_securite",
    category: "conformite",
    phases: ["exitup"],
    minTier: 4,
    docForgeReady: false,
    source: "nouveau",
  },
  {
    id: "TPL-CISOB-N04",
    name: "Rapport Post-Mortem d'Incident de Securite",
    description: "Analyse post-incident avec chronologie, causes fondamentales et plan de remediation.",
    botCode: "CISOB",
    blueprintSection: "incidents_reponse",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },

  // ── Templates supplementaires DeepSearch — gaps additionnels identifies ──

  // CEOB extras
  {
    id: "TPL-CEOB-N05",
    name: "Plan Strategique V/TO (Vision/Traction Organizer)",
    description: "Document de planification strategique combinant vision a long terme et execution trimestrielle (EOS).",
    botCode: "CEOB",
    blueprintSection: "objectifs_vitaa",
    category: "strategique",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CEOB-N06",
    name: "Convention d'Actionnaires",
    description: "Document encadrant les droits, obligations et mecanismes de resolution entre actionnaires.",
    botCode: "CEOB",
    blueprintSection: "gouvernance",
    category: "conformite",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CEOB-N07",
    name: "Scorecard Trimestrielle (OKR/Rocks)",
    description: "Tableau de bord trimestriel de suivi des Rocks et OKR avec indicateurs de progression.",
    botCode: "CEOB",
    blueprintSection: "kpis_direction",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CEOB-N08",
    name: "Code d'Ethique et de Conduite",
    description: "Document definissant les standards ethiques et le code de conduite de l'entreprise.",
    botCode: "CEOB",
    blueprintSection: "culture_esg",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },

  // CFOB extras
  {
    id: "TPL-CFOB-N05",
    name: "Gabarit d'Etats Financiers Standardises",
    description: "Templates standardises pour bilan, etat des resultats et flux de tresorerie.",
    botCode: "CFOB",
    blueprintSection: "etats_financiers",
    category: "operationnel",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 1,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CFOB-N06",
    name: "Budget Annuel et Previsions Detaillees",
    description: "Template de budget annuel avec decomposition CAPEX/OPEX et scenarios best/base/worst.",
    botCode: "CFOB",
    blueprintSection: "budget_previsions",
    category: "strategique",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 1,
    docForgeReady: true,
    source: "nouveau",
  },

  // COOB extras
  {
    id: "TPL-COOB-N05",
    name: "Grille de Selection et Evaluation Fournisseurs",
    description: "Grille multicritere pour la selection initiale et l'evaluation periodique des fournisseurs.",
    botCode: "COOB",
    blueprintSection: "gestion_fournisseurs",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-COOB-N06",
    name: "Plan de Capacite Industrielle",
    description: "Analyse de la capacite industrielle avec identification des contraintes et plan d'expansion.",
    botCode: "COOB",
    blueprintSection: "logistique",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },

  // CHROB extras
  {
    id: "TPL-CHROB-N05",
    name: "Manuel de l'Employe",
    description: "Guide complet des politiques internes, droits et obligations de l'employe.",
    botCode: "CHROB",
    blueprintSection: "culture_engagement",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CHROB-N06",
    name: "Programme de Marque Employeur",
    description: "Strategie d'attraction et de retention des talents via la marque employeur.",
    botCode: "CHROB",
    blueprintSection: "recrutement",
    category: "strategique",
    phases: ["scaleup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },

  // CINOB extras
  {
    id: "TPL-CINOB-N04",
    name: "Business Case Innovation Structure",
    description: "Template de business case pour valider une initiative d'innovation avec ROI, risques et Go/No-Go.",
    botCode: "CINOB",
    blueprintSection: "pipeline_innovation",
    category: "operationnel",
    phases: ["startup", "scaleup"],
    minTier: 2,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CINOB-N05",
    name: "Registre de Propriete Intellectuelle (Brevets et Marques)",
    description: "Inventaire formalise des brevets, marques deposees et secrets commerciaux avec dates de renouvellement.",
    botCode: "CINOB",
    blueprintSection: "propriete_intellectuelle",
    category: "conformite",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "nouveau",
  },

  // CROB extras
  {
    id: "TPL-CROB-N05",
    name: "Rapport de Pipeline et Funnel de Conversion",
    description: "Analyse detaillee du funnel de vente avec taux de conversion par etape et previsions.",
    botCode: "CROB",
    blueprintSection: "pipeline_funnel",
    category: "operationnel",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CROB-N06",
    name: "Guide de Traitement des Objections",
    description: "Repertoire structure des objections courantes avec reponses et contre-arguments valides.",
    botCode: "CROB",
    blueprintSection: "methodologie_vente",
    category: "operationnel",
    phases: ["startup", "scaleup"],
    minTier: 1,
    docForgeReady: true,
    source: "nouveau",
  },

  // CLOB extras
  {
    id: "TPL-CLOB-N05",
    name: "Pacte d'Actionnaires et Convention Unanime",
    description: "Convention encadrant les droits de vote, les clauses de sortie et le rachat entre actionnaires.",
    botCode: "CLOB",
    blueprintSection: "structure_corporative",
    category: "conformite",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: false,
    source: "nouveau",
  },
  {
    id: "TPL-CLOB-N06",
    name: "Guide des Obligations Contractuelles Sectorielles",
    description: "Reference des obligations legales specifiques au secteur d'activite de l'entreprise.",
    botCode: "CLOB",
    blueprintSection: "reglementaire",
    category: "conformite",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },

  // CISOB extras
  {
    id: "TPL-CISOB-N05",
    name: "Plan de Remediation des Vulnerabilites",
    description: "Plan structure pour corriger les vulnerabilites identifiees avec priorisation et echeancier.",
    botCode: "CISOB",
    blueprintSection: "vulnerabilites",
    category: "operationnel",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CISOB-N06",
    name: "Programme de Simulation Phishing et Formation",
    description: "Programme annuel de simulations de phishing avec suivi des resultats et formations ciblees.",
    botCode: "CISOB",
    blueprintSection: "formation_phishing",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },

  // CPOB extras
  {
    id: "TPL-CPOB-N05",
    name: "Plan de Maintenance Preventive Systematique",
    description: "Programme de maintenance preventive avec calendrier, pieces de rechange et indicateurs MTBF/MTTR.",
    botCode: "CPOB",
    blueprintSection: "equipements",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CPOB-N06",
    name: "Registre des Incidents SST et Quasi-Accidents",
    description: "Registre detaille des incidents et quasi-accidents avec analyse des causes et actions correctives.",
    botCode: "CPOB",
    blueprintSection: "sst",
    category: "conformite",
    phases: ["scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CPOB-N07",
    name: "Guide de Gestion des Stocks (Min/Max et Kanban)",
    description: "Methodologie de gestion des stocks avec niveaux min/max, point de commande et systeme Kanban.",
    botCode: "CPOB",
    blueprintSection: "gestion_stocks",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },

  // CSOB extras
  {
    id: "TPL-CSOB-N06",
    name: "Document de l'Avantage Concurrentiel (Moats)",
    description: "Analyse et documentation des avantages concurrentiels durables (PI, effets reseau, couts de transfert).",
    botCode: "CSOB",
    blueprintSection: "avantage_concurrentiel",
    category: "strategique",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },

  // CTOB extras
  {
    id: "TPL-CTOB-N05",
    name: "Politique de Gestion du Cycle de Vie des Donnees",
    description: "Politique encadrant la retention, l'archivage et la suppression des donnees conformement aux lois.",
    botCode: "CTOB",
    blueprintSection: "architecture_si",
    category: "conformite",
    phases: ["scaleup", "exitup"],
    minTier: 3,
    docForgeReady: true,
    source: "nouveau",
  },
  {
    id: "TPL-CTOB-N06",
    name: "Plan d'Infrastructure et Monitoring",
    description: "Documentation de l'infrastructure serveurs, monitoring, alerting et couts mensuels.",
    botCode: "CTOB",
    blueprintSection: "infrastructure",
    category: "operationnel",
    phases: ["startup", "scaleup", "exitup"],
    minTier: 2,
    docForgeReady: true,
    source: "nouveau",
  },
];

// ── Catalogue complet (209 templates) ──

export const BLUEPRINT_TEMPLATES: BlueprintTemplate[] = [
  ...EXISTING_TEMPLATES,
  ...NEW_TEMPLATES,
];

// ── Helper functions ──

/**
 * Retourne les templates applicables a une sous-section specifique d'un departement.
 */
export function getTemplatesForSection(botCode: string, sectionId: string): BlueprintTemplate[] {
  return BLUEPRINT_TEMPLATES.filter(
    (t) => t.botCode === botCode && t.blueprintSection === sectionId
  );
}

/**
 * Retourne tous les templates d'un departement.
 */
export function getTemplatesForBot(botCode: string): BlueprintTemplate[] {
  return BLUEPRINT_TEMPLATES.filter((t) => t.botCode === botCode);
}

/**
 * Retourne les templates filtres par source (existant ou nouveau).
 */
export function getTemplatesBySource(source: TemplateSource): BlueprintTemplate[] {
  return BLUEPRINT_TEMPLATES.filter((t) => t.source === source);
}

/**
 * Retourne les templates filtres par categorie.
 */
export function getTemplatesByCategory(category: TemplateCategory): BlueprintTemplate[] {
  return BLUEPRINT_TEMPLATES.filter((t) => t.category === category);
}

/**
 * Retourne les templates applicables pour un tier (taille) d'entreprise donne.
 */
export function getTemplatesForTier(tier: number): BlueprintTemplate[] {
  return BLUEPRINT_TEMPLATES.filter((t) => t.minTier <= tier);
}

/**
 * Retourne les templates applicables pour une phase d'entreprise donnee.
 */
export function getTemplatesForPhase(phase: TemplatePhase): BlueprintTemplate[] {
  return BLUEPRINT_TEMPLATES.filter((t) => t.phases.includes(phase));
}

/**
 * Retourne les templates prets pour DocForge.
 */
export function getDocForgeReadyTemplates(): BlueprintTemplate[] {
  return BLUEPRINT_TEMPLATES.filter((t) => t.docForgeReady);
}

/**
 * Statistiques par departement: nombre de templates existants vs nouveaux.
 */
export function getTemplateStats(): Record<string, { existant: number; nouveau: number; total: number }> {
  const stats: Record<string, { existant: number; nouveau: number; total: number }> = {};
  for (const t of BLUEPRINT_TEMPLATES) {
    if (!stats[t.botCode]) {
      stats[t.botCode] = { existant: 0, nouveau: 0, total: 0 };
    }
    stats[t.botCode][t.source]++;
    stats[t.botCode].total++;
  }
  return stats;
}
